import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleG = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5" aria-label={`${n} star rating`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} className={`h-5 w-5 ${s <= Math.round(n) ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`} />
    ))}
  </div>
);

export const GoogleReviews = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reviews/google`).then((res) => setData(res.data)).catch(() => setData({ available: false }));
  }, []);

  const profileUrl = data?.maps_url || data?.profile_url || "https://share.google/GkpcJx5lAmfqkljEA";
  const reviewUrl = data?.review_url || profileUrl;

  return (
    <section className="py-24 sm:py-28 border-b border-zinc-800/60" data-testid="google-reviews-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500">Google Reviews</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-white mt-4 mb-4">
            See Why Las Vegas Drivers Choose Mason's Mobile
          </h2>
          <div className="flex items-center gap-3 mb-8">
            <GoogleG className="h-7 w-7" />
            <Stars n={data?.rating || 5} />
            {data?.available && data?.rating ? (
              <span className="text-zinc-300 text-sm font-semibold" data-testid="google-rating-text">
                {data.rating.toFixed(1)} on Google{data.total ? ` · ${data.total} reviews` : ""}
              </span>
            ) : (
              <span className="text-zinc-400 text-sm" data-testid="google-rating-text">Rated by drivers across the Las Vegas Valley</span>
            )}
          </div>
        </div>

        {data?.available && data?.reviews?.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10" data-testid="google-reviews-list">
            {data.reviews.map((r) => (
              <div key={`${r.author}-${r.when}`} className="bg-[#121212] border border-zinc-800 rounded-xl p-5">
                <Stars n={r.rating} />
                <p className="text-zinc-300 text-sm leading-relaxed mt-3 mb-3">"{r.text}"</p>
                <span className="font-heading font-semibold text-white text-sm">{r.author}</span>
                <span className="text-xs text-zinc-500 block">{r.when} · Google</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
          <a
            data-testid="read-google-reviews-btn"
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-zinc-900 hover:bg-zinc-200 rounded-sm px-6 py-4 font-semibold text-sm sm:text-base transition-colors"
          >
            <GoogleG className="h-5 w-5" /> Read Our Google Reviews <ExternalLink className="h-4 w-4" />
          </a>
          <a
            data-testid="leave-google-review-btn"
            href={reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-4 font-semibold text-sm sm:text-base transition-colors shadow-lg shadow-blue-600/25"
          >
            <PenLine className="h-5 w-5" /> Leave Mason's Mobile a Google Review
          </a>
        </div>
      </div>
    </section>
  );
};

export const ReviewCTA = () => {
  const [urls, setUrls] = useState(null);
  useEffect(() => {
    axios.get(`${API}/business/profile`).then((res) => setUrls(res.data)).catch(() => {});
  }, []);
  return (
    <section className="py-16 border-b border-zinc-800/60" data-testid="review-cta-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="bg-[#121212] border border-zinc-800 rounded-xl p-8 sm:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-2">Happy with your service?</h2>
            <p className="text-zinc-400">Help other Las Vegas drivers find Mason's Mobile.</p>
          </div>
          <a
            data-testid="bottom-leave-review-btn"
            href={urls?.google_review_url || "https://share.google/GkpcJx5lAmfqkljEA"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-8 py-4 font-semibold shrink-0 transition-colors"
          >
            <GoogleG className="h-5 w-5" /> Leave a Google Review
          </a>
        </div>
      </div>
    </section>
  );
};
